import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { createTextAnnotator, W3CTextFormat } from "@recogito/text-annotator";
import type { TextAnnotation, TextAnnotator, W3CTextAnnotation } from "@recogito/text-annotator";
import type { Color } from "@recogito/text-annotator";
import type { AnnotationState } from "@annotorious/core";
import "@recogito/text-annotator/text-annotator.css";
import type { AnnotationSelection, LabelFile } from "../types";
import { getTranscript, getLabelFile, saveDraft, saveCompleted, deleteDraft } from "../api/s3";

const ANNOTATION_COLORS = [
  "#fde047", // yellow-300
  "#fb923c", // orange-400
  "#f87171", // red-400
  "#4ade80", // green-400
  "#60a5fa", // blue-400
  "#c084fc", // purple-400
  "#fbbf24", // amber-400
  "#2dd4bf", // teal-400
];

interface Props {
  fileId: string | null;
  username: string;
  activeAnnotationId: string | null;
  selections: AnnotationSelection[];
  onAnnotationSelect: (id: string | null) => void;
  onSelectionsChange: (selections: AnnotationSelection[]) => void;
  onDraftSaved: () => void;
  onCompleted: () => void;
}

export default function TranscriptViewer({
  fileId,
  username,
  activeAnnotationId,
  selections,
  onAnnotationSelect,
  onSelectionsChange,
  onDraftSaved,
  onCompleted,
}: Props) {
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const annoRef = useRef<TextAnnotator<TextAnnotation, W3CTextAnnotation> | null>(null);
  const colorMapRef = useRef<Map<string, number>>(new Map());
  // Keep a ref to current selections so event handlers always see the latest value
  const selectionsRef = useRef<AnnotationSelection[]>(selections);
  selectionsRef.current = selections;

  // Load transcript when fileId changes
  useEffect(() => {
    if (!fileId) {
      setMarkdown(null);
      return;
    }
    setLoading(true);
    setError(null);
    setMarkdown(null);
    getTranscript(fileId)
      .then(setMarkdown)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [fileId]);

  // Init recogito after markdown renders into the DOM
  useEffect(() => {
    if (!markdown || !contentRef.current || !fileId) return;

    if (annoRef.current) {
      annoRef.current.destroy();
      annoRef.current = null;
      colorMapRef.current.clear();
    }

    const anno = createTextAnnotator(contentRef.current, {
      adapter: W3CTextFormat(`/transcripts/${fileId}`, contentRef.current),
    });
    annoRef.current = anno;

    // Dynamic color per annotation index
    anno.setStyle((annotation: TextAnnotation, state: AnnotationState) => {
      const idx = colorMapRef.current.get(annotation.id) ?? 0;
      const fill = ANNOTATION_COLORS[idx % ANNOTATION_COLORS.length] as Color;
      return {
        fill,
        fillOpacity: state?.selected ? 0.8 : 0.5,
      };
    });

    anno.on("createAnnotation", (annotation: W3CTextAnnotation) => {
      const nextIdx = colorMapRef.current.size;
      colorMapRef.current.set(annotation.id, nextIdx);

      const selection: AnnotationSelection = {
        id: annotation.id,
        target: annotation.target as AnnotationSelection["target"],
        labels: [],
        reasoning: "",
      };
      onSelectionsChange([...selectionsRef.current, selection]);
      onAnnotationSelect(annotation.id);
    });

    anno.on("selectionChanged", (annotations: W3CTextAnnotation[]) => {
      onAnnotationSelect(annotations?.[0]?.id ?? null);
    });

    // Restore existing label file annotations.
    // cancelled flag prevents a stale promise from a previous render
    // from adding annotations to a newly created annotator instance.
    let cancelled = false;
    getLabelFile(fileId).then((labelFile) => {
      if (cancelled || !labelFile || !annoRef.current) return;
      labelFile.selections.forEach((sel, idx) => {
        colorMapRef.current.set(sel.id, idx);
        annoRef.current!.addAnnotation({
          "@context": "http://www.w3.org/ns/anno.jsonld",
          id: sel.id,
          type: "Annotation",
          body: [],
          target: sel.target,
        } as unknown as W3CTextAnnotation);
      });
      onSelectionsChange(labelFile.selections);
    });

    return () => {
      cancelled = true;
      anno.destroy();
      annoRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markdown, fileId]);

  // Sync active selection highlight with recogito
  useEffect(() => {
    if (!annoRef.current) return;
    annoRef.current.setSelected(activeAnnotationId ?? undefined);
  }, [activeAnnotationId]);

  async function handleSaveDraft() {
    if (!fileId) return;
    setSaving(true);
    try {
      const labelFile: LabelFile = {
        transcriptionFile: `/transcripts/${fileId}`,
        completed: null,
        username,
        selections: selectionsRef.current,
      };
      await saveDraft(fileId, labelFile);
      onDraftSaved();
    } finally {
      setSaving(false);
    }
  }

  async function handleComplete() {
    if (!fileId) return;
    const hasLabel = selectionsRef.current.some((s) => s.labels.length > 0);
    if (!hasLabel) return;
    if (!window.confirm("Mark this transcript as completed? This cannot be undone.")) return;

    setSaving(true);
    try {
      const labelFile: LabelFile = {
        transcriptionFile: `/transcripts/${fileId}`,
        completed: new Date().toISOString(),
        username,
        selections: selectionsRef.current,
      };
      await saveCompleted(fileId, labelFile);
      await deleteDraft(fileId);
      if (annoRef.current) {
        annoRef.current.destroy();
        annoRef.current = null;
        colorMapRef.current.clear();
      }
      onCompleted();
    } finally {
      setSaving(false);
    }
  }

  if (!fileId) {
    return (
      <div style={{ ...containerStyle, alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: "#999" }}>Select a transcript to begin</p>
      </div>
    );
  }

  if (loading) {
    return <div style={containerStyle}><p style={{ padding: 20, color: "#999" }}>Loading...</p></div>;
  }

  if (error) {
    return <div style={containerStyle}><p style={{ padding: 20, color: "red" }}>{error}</p></div>;
  }

  const hasLabel = selections.some((s) => s.labels.length > 0);

  return (
    <div style={containerStyle}>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        <div ref={contentRef}>
          <ReactMarkdown>{markdown ?? ""}</ReactMarkdown>
        </div>
      </div>

      <div style={footerStyle}>
        <button onClick={handleSaveDraft} disabled={saving} style={draftBtnStyle}>
          {saving ? "Saving..." : "Save Draft"}
        </button>
        <button
          onClick={handleComplete}
          disabled={saving || !hasLabel}
          title={!hasLabel ? "Add at least one label before completing" : undefined}
          style={completeBtnStyle(!hasLabel || saving)}
        >
          Completed
        </button>
      </div>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  borderRight: "1px solid #e0e0e0",
  overflow: "hidden",
};

const footerStyle: React.CSSProperties = {
  display: "flex",
  gap: 10,
  padding: "10px 16px",
  borderTop: "1px solid #e0e0e0",
  flexShrink: 0,
  background: "#fff",
};

const draftBtnStyle: React.CSSProperties = {
  padding: "7px 18px",
  background: "#fff",
  border: "1px solid #f59e0b",
  color: "#b45309",
  borderRadius: 4,
  cursor: "pointer",
  fontWeight: 500,
};

function completeBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "7px 18px",
    background: disabled ? "#f4f4f4" : "#22c55e",
    border: `1px solid ${disabled ? "#ccc" : "#16a34a"}`,
    color: disabled ? "#aaa" : "#fff",
    borderRadius: 4,
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 500,
  };
}
